/**
 * @file src/pages/404.tsx
 * @exports ErrorPage
 * @summary Custom 404 page offering authenticated applicants recovery for pending, accepted, and started vID work.
 * @remarks Uses Cortex's applicant-scoped voucher RLS and preserves the normal 404 experience when no resumable voucher exists.
 */

import dynamic from "src/lib/dynamic";
import { useState } from "react";

import { paths } from "src/const";
import { SortOrder, useMeQuery, useResumableVidVouchersQuery, VoucherStatusEnum } from "src/generated/graphql";
import { UserRoleEnum } from "src/types";

// Dynamically import the NotFound component to enable code splitting
const NotFound = dynamic(() => import("@cerebruminc/cerebellum").then((mod) => mod.NotFound), { ssr: false });

const ErrorPage = () => {
	const [loadedAt] = useState(() => new Date().toISOString());
	const { data: userData } = useMeQuery();
	const user = userData?.me;
	const isApplicant = user?.roleAssignment?.some(({ roleV2 }) => roleV2.name === UserRoleEnum.User) ?? false;
	const { data: voucherData } = useResumableVidVouchersQuery(
		{
			orderBy: { createdAt: SortOrder.Desc },
			take: 1,
			where: {
				email: { equals: user?.email ?? "" },
				expiresAt: { gt: loadedAt },
				orderId: { equals: null },
				status: { in: [VoucherStatusEnum.Pending, VoucherStatusEnum.Accepted, VoucherStatusEnum.Started] },
			},
		},
		{ enabled: isApplicant && Boolean(user?.email) },
	);
	const resumableVoucher = isApplicant ? voucherData?.vIDVouchers[0] : undefined;
	const recoveryUrl = resumableVoucher
		? `${paths.vidWelcome}?voucherId=${encodeURIComponent(resumableVoucher.id)}`
		: paths.home;

	return (
		<div className="w-screen h-screen">
			<NotFound
				buttonText={resumableVoucher ? "Continue identity verification" : undefined}
				headerText={resumableVoucher ? "Your identity verification is waiting" : undefined}
				subheaderText={resumableVoucher ? "Continue where you left off to complete your request." : undefined}
				url={recoveryUrl}
			/>
		</div>
	);
};

export default ErrorPage;
